import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Observable, startWith, take } from 'rxjs';
import { NotificationService } from '@htx/core';
import { PolicyBuilderComponent } from '../policy-builder/policy-builder.component';
import { Constraint } from '../../../shared/models/constraint.model';
import { Policy, PolicyType, PolicyRole } from '../../../shared/models/policy.model';
import { PolicyService } from '../../../shared/services/policy.service';

@Component({
  selector: 'app-create-policy-page',
  imports: [
    ReactiveFormsModule,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslocoPipe,
    PolicyBuilderComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './create-policy-builder-dialog.component.html',
  styleUrl: './create-policy-builder-dialog.component.scss'
})
export class CreatePolicyPageComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly policyService = inject(PolicyService);
  private readonly notificationService = inject(NotificationService);
  private readonly translocoService = inject(TranslocoService);

  /** Set when the page is opened in edit mode (route /policies/edit/:id). */
  readonly editId = signal<string | null>(null);
  readonly isEdit = computed(() => this.editId() !== null);

  /** Existing private properties, preserved across a full-replace update. */
  private loadedPrivateProperties: Policy['privateProperties'];

  readonly role = signal<PolicyRole>('usage');
  readonly constraints = signal<Constraint[]>([
    { type: PolicyType.MEMBERSHIP_STATIC },
    { type: PolicyType.FRAMEWORK_AGREEMENT },
    { type: PolicyType.USE_CASE_MEMBERSHIP, useCaseId: 'UC.digitalization.sc' }
  ]);
  readonly builderValid = signal(true);
  readonly saving = signal(false);

  readonly policyIdCtrl = new FormControl('', [
    Validators.required,
    Validators.pattern(/^[a-zA-Z0-9._-]+$/)
  ]);

  private readonly policyId = toSignal(
    this.policyIdCtrl.valueChanges.pipe(startWith(this.policyIdCtrl.value)),
    { initialValue: '' }
  );

  readonly isValid = computed(() => {
    // Read policyId signal to make computed reactive to FormControl changes
    this.policyId();
    return this.constraints().length > 0 && this.builderValid() && this.policyIdCtrl.valid;
  });

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;

    this.editId.set(id);
    this.policyIdCtrl.setValue(id);

    const existing = this.policyService.getPolicyById(id);
    if (existing) {
      this.prefill(existing);
    } else {
      // Deep link / page reload: snapshot may be empty, so reload first.
      this.policyService
        .reloadPolicies()
        .pipe(take(1))
        .subscribe(() => {
          const policy = this.policyService.getPolicyById(id);
          if (policy) {
            this.prefill(policy);
          } else {
            this.notificationService.showError(
              this.translocoService.translate('policy.editForm.notFound', { policy: { id } }),
              5000
            );
            void this.router.navigate(['/policies']);
          }
        });
    }
  }

  private prefill(policy: Policy): void {
    this.role.set(policy.role ?? 'usage');
    this.constraints.set(policy.constraints ?? []);
    this.loadedPrivateProperties = policy.privateProperties;
  }

  selectRole(r: PolicyRole): void {
    this.role.set(r);
    if (r === 'access') {
      this.constraints.set([{ type: PolicyType.MEMBERSHIP_STATIC }]);
    } else {
      this.constraints.set([
        { type: PolicyType.MEMBERSHIP_STATIC },
        { type: PolicyType.FRAMEWORK_AGREEMENT },
        { type: PolicyType.USE_CASE_MEMBERSHIP, useCaseId: 'UC.digitalization.sc' }
      ]);
    }
  }

  onConstraintsChange(constraints: Constraint[]): void {
    this.constraints.set(constraints);
  }

  onBuilderValidChange(valid: boolean): void {
    this.builderValid.set(valid);
  }

  cancel(): void {
    void this.router.navigate(['/policies']);
  }

  submit(): void {
    if (!this.isValid() || this.saving()) return;

    this.saving.set(true);

    const editId = this.editId();
    const policy: Policy = {
      id: editId ?? this.policyId()?.trim() ?? '',
      createdAt: Date.now(),
      constraints: this.constraints(),
      prohibition: [],
      obligation: [],
      role: this.role(),
      privateProperties: this.loadedPrivateProperties
    };

    const request$: Observable<unknown> = editId
      ? this.policyService.editPolicy(editId, policy, this.role())
      : this.policyService.createPolicy(policy, this.role());

    request$.pipe(take(1)).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          this.translocoService.translate(
            editId ? 'policy.editForm.successMessage' : 'policy.creationForm.successMessage'
          )
        );
        void this.router.navigate(['/policies']);
      },
      error: (error) => {
        this.saving.set(false);
        console.error('[CreatePolicyPage] Policy save failed:', error.status, error.error);
        if (!editId && error.status === 409) {
          this.notificationService.showError(
            this.translocoService.translate('policy.creationForm.errorMessage.duplicate'),
            5000
          );
        } else {
          const detail = this.extractErrorDetail(error);
          const message = detail
            ? `${this.translocoService.translate('policy.creationForm.errorMessage.unbuildable')}: ${detail}`
            : this.translocoService.translate('policy.creationForm.errorMessage.unbuildable');
          this.notificationService.showError(message, 8000);
        }
      }
    });
  }

  private extractErrorDetail(err: unknown): string | null {
    const httpErr = err as { error?: unknown };
    const body = httpErr?.error;
    if (Array.isArray(body) && body.length > 0 && body[0]?.message) {
      return body[0].message;
    }
    if (body && typeof body === 'object' && 'message' in body) {
      return (body as { message: string }).message;
    }
    return null;
  }
}
